import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { mcPost, money } from "./api";

export const PricePanel = ({ token, d, center, perms, onChanged, open, setOpen }) => {
  const [f, setF] = useState({ price: d.price, effective_date: new Date().toISOString().slice(0, 10), reason: "" });
  const cur = d.currency;
  const run = async (path, body, msg) => {
    try {
      const r = await mcPost(path, { token, menu_item_id: d.menu_item_id, center, ...body });
      toast.success(msg);
      if (r.sync) toast.message(r.sync.map((s) => `${s.destination}: ${s.status}`).join(" · "));
      setOpen(false); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  const save = () => perms.can_edit_price
    ? run("price/save", { price: parseFloat(f.price), effective_date: f.effective_date, reason: f.reason }, "Price saved")
    : run("price/request", { price: parseFloat(f.price), effective_date: f.effective_date, reason: f.reason }, "Price-change request submitted for approval");
  const act = (action) => {
    const reason = window.prompt(`Reason for ${action.replace("_", " ").toLowerCase()}:`);
    if (reason) run("price/action", { action, reason }, `${action} done`);
  };
  return (
    <div className="rounded border border-stone-200 p-3 space-y-2" data-testid="price-panel">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <div><p className="text-stone-500">Centre selling price · {center}</p><p className="font-bold" data-testid="detail-centre-price">{money(d.price, cur)}</p></div>
        <div><p className="text-stone-500">Selling price (incl GST)</p><p className="font-bold" data-testid="detail-price-incl">{money(d.price_incl, cur)}</p></div>
        <div><p className="text-stone-500">Excl GST ({d.gst_pct}% {d.gst_type})</p><p className="font-bold" data-testid="detail-price-excl">{money(d.price_excl, cur)}</p></div>
        <div><p className="text-stone-500">Source</p><p className="font-semibold" data-testid="detail-price-source">{d.price_source === "CENTRE" ? "Centre price" : "Base price (no centre override)"}{!d.available && " · DISABLED"}</p></div>
        {perms.can_view_suggested && <div><p className="text-stone-500">Suggested (planning only)</p><p className="font-bold text-amber-800" data-testid="detail-suggested">{money(d.suggested_incl, cur)} <span className="text-[10px] font-normal">incl GST</span></p></div>}
      </div>
      {(perms.can_edit_price || perms.can_request) && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setOpen(!open)} data-testid="edit-price-btn">{perms.can_edit_price ? "Edit Centre Price" : "Request Centre Price Change"}</Button>
          {perms.can_edit_price && <>
            <Button size="sm" variant="outline" onClick={() => act("RESTORE")} data-testid="restore-price-btn">Restore previous</Button>
            <Button size="sm" variant="outline" onClick={() => act(d.available ? "DISABLE" : "ENABLE")} data-testid="toggle-available-btn">{d.available ? "Disable at centre" : "Enable at centre"}</Button>
            {d.price_source === "CENTRE" && <Button size="sm" variant="outline" className="text-red-700" onClick={() => act("DELETE_OVERRIDE")} data-testid="delete-override-btn">Delete centre override</Button>}
          </>}
        </div>
      )}
      {open && (
        <div className="grid md:grid-cols-[120px_150px_1fr_auto] gap-2" data-testid="price-edit-form">
          <Input type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="price-input" />
          <Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="price-effective-date" />
          <Input placeholder="Reason (mandatory)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="price-reason" />
          <Button size="sm" onClick={save} data-testid="price-save-btn">{perms.can_edit_price ? "Save price" : "Submit request"}</Button>
          <p className="md:col-span-4 text-[10px] text-stone-500">Price is stored as configured for this centre ({d.gst_type} of GST). Historical orders keep their original prices.</p>
        </div>
      )}
      {perms.can_view_audit && d.price_history?.length > 0 && (
        <ul className="max-h-32 overflow-y-auto text-[11px] space-y-0.5" data-testid="price-history">
          {d.price_history.filter((h) => h.center !== "*BASE*").map((h) => (
            <li key={h.id}>{h.at?.slice(0, 16).replace("T", " ")} · {h.by} · <b>{h.action}</b> {h.old_price ?? "—"} → {h.new_price ?? "—"} (eff. {h.effective_date}, {h.status}) — {h.reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const BasePricePanel = ({ token, d, perms, onChanged, open, setOpen }) => {
  const [f, setF] = useState({ price: d.base_price, effective_date: new Date().toISOString().slice(0, 10), reason: "" });
  const save = async () => {
    try {
      const r = await mcPost("base-price/save", { token, menu_item_id: d.menu_item_id, price: parseFloat(f.price), effective_date: f.effective_date, reason: f.reason });
      toast.success(r.status === "APPLIED" ? "Base price saved (centre overrides unchanged)" : `Base price scheduled for ${r.effective_date}`);
      setOpen(false); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="rounded border border-stone-200 p-3 space-y-2" data-testid="base-price-panel">
      <div className="flex items-center justify-between text-xs">
        <p>Current Base Price (default for all centres): <b data-testid="detail-base-price">{money(d.base_price, d.currency)}</b></p>
        {perms.can_edit_price && <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="edit-base-price-btn">Edit Base Price</Button>}
      </div>
      {open && (
        <div className="grid md:grid-cols-[120px_150px_1fr_auto] gap-2" data-testid="base-price-form">
          <Input type="number" step="0.01" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid="base-price-input" />
          <Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} data-testid="base-price-effective-date" />
          <Input placeholder="Reason (mandatory)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="base-price-reason" />
          <Button size="sm" onClick={save} data-testid="base-price-save-btn">Save base price</Button>
          <p className="md:col-span-4 text-[10px] text-stone-500">Centre-specific prices are not overwritten. Suggested price stays advisory.</p>
        </div>
      )}
    </div>
  );
};
